import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  PenLine, Trash2, Pencil, Clock, Search, Sparkles, Plus,
  RotateCcw, Archive, Eye, EyeOff, Save, X, Loader2, Rss, Link2, Import, ExternalLink
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["General", "Home Insurance", "Auto Insurance", "Commercial", "Life Insurance", "Health Insurance", "Annuities", "LegalShield", "Long-term Care"];

function BlogRow({ blog, onEdit, onTrash, onTogglePublish }) {
  const ts = blog.created_at ? new Date(blog.created_at).toLocaleString() : "";
  return (
    <div data-testid={`blog-row-${blog.id}`} className="border border-slate-100 rounded-lg hover:border-slate-200 transition-colors">
      <div className="flex items-center gap-3 p-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${blog.source === "rss" ? "bg-orange-50" : "bg-indigo-50"}`}>
          {blog.source === "rss" ? <Rss className="h-4 w-4 text-orange-500" /> : <PenLine className="h-4 w-4 text-indigo-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-slate-800 truncate">{blog.title}</p>
            <Badge variant="outline" className="text-[10px] shrink-0">{blog.category}</Badge>
            {!blog.published && <Badge className="text-[10px] bg-amber-100 text-amber-700 border-0 shrink-0">Draft</Badge>}
            {blog.source === "rss" && <Badge className="text-[10px] bg-orange-100 text-orange-700 border-0 shrink-0">RSS</Badge>}
          </div>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{blog.excerpt}</p>
          <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-400">
            {ts && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{ts}</span>}
            {blog.tags?.length > 0 && <span>{blog.tags.join(", ")}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button data-testid={`toggle-publish-${blog.id}`} onClick={() => onTogglePublish(blog)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" title={blog.published ? "Unpublish" : "Publish"}>
            {blog.published ? <Eye className="h-3.5 w-3.5 text-emerald-500" /> : <EyeOff className="h-3.5 w-3.5 text-slate-400" />}
          </button>
          <button data-testid={`edit-blog-${blog.id}`} onClick={() => onEdit(blog)} className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors" title="Edit blog">
            <Pencil className="h-3.5 w-3.5 text-slate-400 hover:text-blue-600" />
          </button>
          <button data-testid={`trash-blog-${blog.id}`} onClick={() => onTrash(blog.id)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Move to trash">
            <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
          </button>
        </div>
      </div>
    </div>
  );
}

function TrashedBlogRow({ blog, onRestore, onDelete }) {
  const ts = blog.trashed_at ? new Date(blog.trashed_at).toLocaleString() : "";
  return (
    <div data-testid={`trashed-blog-${blog.id}`} className="flex items-center gap-3 p-3 border border-red-100 rounded-lg bg-red-50/30">
      <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center shrink-0">
        <Trash2 className="h-4 w-4 text-red-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-600 truncate">{blog.title}</p>
        <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-400">
          <span>{blog.category}</span>
          {ts && <span>Trashed: {ts}</span>}
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        <button data-testid={`restore-blog-${blog.id}`} onClick={() => onRestore(blog.id)} className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors">Restore</button>
        <button data-testid={`delete-blog-${blog.id}`} onClick={() => onDelete(blog.id)} className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors">Delete Forever</button>
      </div>
    </div>
  );
}

function EditBlogDialog({ blog, open, onClose, onSave }) {
  const [form, setForm] = useState({ title: "", excerpt: "", content: "", category: "General", tags: "", published: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (blog) {
      setForm({
        title: blog.title || "",
        excerpt: blog.excerpt || "",
        content: blog.content || "",
        category: blog.category || "General",
        tags: (blog.tags || []).join(", "),
        published: blog.published !== false,
      });
    }
  }, [blog]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(blog.id, { ...form, tags: form.tags.split(",").map(t => t.trim()).filter(Boolean) });
      onClose();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-blog-dialog">
        <DialogHeader><DialogTitle>Edit Blog Post</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs font-semibold text-slate-600">Title</Label>
            <Input data-testid="edit-blog-title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Category</Label>
              <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                <SelectTrigger data-testid="edit-blog-category" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Tags (comma-separated)</Label>
              <Input data-testid="edit-blog-tags" value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} className="mt-1" placeholder="insurance, tips, home" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Excerpt</Label>
            <Textarea data-testid="edit-blog-excerpt" value={form.excerpt} onChange={e => setForm(p => ({ ...p, excerpt: e.target.value }))} className="mt-1" rows={2} />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Content (Markdown)</Label>
            <Textarea data-testid="edit-blog-content" value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} className="mt-1 font-mono text-xs" rows={12} />
          </div>
          <div className="flex items-center gap-2">
            <Switch data-testid="edit-blog-published" checked={form.published} onCheckedChange={v => setForm(p => ({ ...p, published: v }))} />
            <Label className="text-xs text-slate-500">Published</Label>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline" size="sm">Cancel</Button></DialogClose>
          <Button data-testid="save-blog-btn" size="sm" onClick={handleSave} disabled={saving} className="bg-[#002147] hover:bg-[#001a3a]">
            <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GenerateBlogDialog({ open, onClose, onGenerated }) {
  const [topic, setTopic] = useState("");
  const [category, setCategory] = useState("General");
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    if (!topic.trim()) return;
    setGenerating(true);
    try {
      await axios.post(`${API}/admin/blogs/generate`, { topic, category }, { withCredentials: true });
      setTopic("");
      onClose();
      onGenerated();
    } catch (e) {
      console.error("Failed to generate blog", e);
      alert("Failed to generate blog. Please try again.");
    } finally { setGenerating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v && !generating) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="generate-blog-dialog">
        <DialogHeader><DialogTitle>Generate AI Blog Post</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs font-semibold text-slate-600">Topic</Label>
            <Input data-testid="gen-blog-topic" value={topic} onChange={e => setTopic(e.target.value)} className="mt-1" placeholder="e.g., Tips for first-time homebuyers insurance" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="gen-blog-category" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline" size="sm" disabled={generating}>Cancel</Button></DialogClose>
          <Button data-testid="submit-gen-blog-btn" size="sm" onClick={handleGenerate} disabled={generating || !topic.trim()} className="bg-[#D4AF37] hover:bg-[#B5952F] text-[#002147]">
            {generating ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Generating...</> : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Generate</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===================== RSS SECTION =====================

function RssSection({ onImported }) {
  const [feeds, setFeeds] = useState([]);
  const [newUrl, setNewUrl] = useState("");
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [selectedFeed, setSelectedFeed] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [importing, setImporting] = useState(null);
  const [importCategory, setImportCategory] = useState("General");

  const loadFeeds = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/rss/feeds`, { withCredentials: true });
      setFeeds(res.data.feeds || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadFeeds(); }, [loadFeeds]);

  const addFeed = async () => {
    if (!newUrl.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/admin/rss/feeds`, { url: newUrl, name: newName }, { withCredentials: true });
      setNewUrl("");
      setNewName("");
      await loadFeeds();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to add feed");
    } finally { setAdding(false); }
  };

  const deleteFeed = async (feedId) => {
    if (!window.confirm("Remove this RSS feed?")) return;
    await axios.delete(`${API}/admin/rss/feeds/${feedId}`, { withCredentials: true });
    if (selectedFeed?.id === feedId) { setSelectedFeed(null); setArticles([]); }
    await loadFeeds();
  };

  const fetchArticles = async (feed) => {
    setSelectedFeed(feed);
    setLoadingArticles(true);
    try {
      const res = await axios.get(`${API}/admin/rss/feeds/${feed.id}/articles`, { withCredentials: true });
      setArticles(res.data.articles || []);
    } catch (e) { console.error(e); setArticles([]); }
    finally { setLoadingArticles(false); }
  };

  const importArticle = async (article) => {
    setImporting(article.title);
    try {
      await axios.post(`${API}/admin/rss/import`, {
        title: article.title,
        excerpt: article.summary?.slice(0, 300) || "",
        content: article.content || article.summary || "",
        category: importCategory,
        tags: [importCategory, "RSS Import"],
        source_url: article.link,
      }, { withCredentials: true });
      onImported();
      alert(`"${article.title}" imported as blog post!`);
    } catch (e) {
      alert("Failed to import: " + (e.response?.data?.detail || e.message));
    } finally { setImporting(null); }
  };

  return (
    <div className="space-y-4" data-testid="rss-section">
      {/* Add Feed */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Rss className="h-4 w-4 text-orange-500" /> RSS Feeds</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs font-semibold text-slate-600">Feed URL</Label>
              <Input data-testid="rss-url-input" value={newUrl} onChange={e => setNewUrl(e.target.value)} className="mt-1 text-sm" placeholder="https://example.com/feed.xml" />
            </div>
            <div className="w-40">
              <Label className="text-xs font-semibold text-slate-600">Name (optional)</Label>
              <Input data-testid="rss-name-input" value={newName} onChange={e => setNewName(e.target.value)} className="mt-1 text-sm" placeholder="Feed name" />
            </div>
            <Button data-testid="add-rss-btn" size="sm" onClick={addFeed} disabled={adding || !newUrl.trim()} className="bg-[#002147] hover:bg-[#001a3a] text-xs h-9 shrink-0">
              {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1" /> Add</>}
            </Button>
          </div>

          {/* Feeds List */}
          {feeds.length === 0 ? (
            <p className="text-xs text-slate-400 py-4 text-center">No RSS feeds added yet. Add a feed URL above to get started.</p>
          ) : (
            <div className="space-y-1.5">
              {feeds.map(feed => (
                <div key={feed.id} data-testid={`rss-feed-${feed.id}`}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${selectedFeed?.id === feed.id ? "border-orange-300 bg-orange-50" : "border-slate-100 hover:border-slate-200"}`}
                  onClick={() => fetchArticles(feed)}
                >
                  <Rss className="h-4 w-4 text-orange-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{feed.name}</p>
                    <p className="text-[11px] text-slate-400 truncate">{feed.url}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px] shrink-0">{feed.article_count || 0} articles</Badge>
                  <button data-testid={`delete-rss-${feed.id}`} onClick={e => { e.stopPropagation(); deleteFeed(feed.id); }} className="p-1 rounded hover:bg-red-50 transition-colors shrink-0">
                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Articles from selected feed */}
      {selectedFeed && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ExternalLink className="h-4 w-4 text-slate-400" /> {selectedFeed.name}
                <Badge variant="outline" className="text-[10px]">{articles.length} articles</Badge>
              </CardTitle>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-500">Import as:</Label>
                <Select value={importCategory} onValueChange={setImportCategory}>
                  <SelectTrigger className="h-7 w-40 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {loadingArticles ? (
              <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            ) : articles.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No articles found in this feed.</p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {articles.map((article, i) => (
                  <div key={i} data-testid={`rss-article-${i}`} className="flex items-start gap-3 p-3 border border-slate-100 rounded-lg hover:border-slate-200 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 line-clamp-1">{article.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{article.summary}</p>
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400">
                        {article.published && <span>{article.published}</span>}
                        {article.author && <span>by {article.author}</span>}
                        {article.link && <a href={article.link} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline flex items-center gap-0.5" onClick={e => e.stopPropagation()}>Source <ExternalLink className="h-2.5 w-2.5" /></a>}
                      </div>
                    </div>
                    <Button data-testid={`import-article-${i}`} size="sm" variant="outline" onClick={() => importArticle(article)}
                      disabled={importing === article.title} className="text-xs h-8 shrink-0 gap-1">
                      {importing === article.title ? <Loader2 className="h-3 w-3 animate-spin" /> : <Import className="h-3 w-3" />}
                      Import
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ===================== MAIN TAB =====================

export default function AdminBlogTab() {
  const [blogs, setBlogs] = useState([]);
  const [trashedBlogs, setTrashedBlogs] = useState([]);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("active");
  const [loading, setLoading] = useState(true);
  const [editBlog, setEditBlog] = useState(null);
  const [genOpen, setGenOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [ar, tr] = await Promise.all([
        axios.get(`${API}/admin/blogs?status=active`, { withCredentials: true }),
        axios.get(`${API}/admin/blogs?status=trashed`, { withCredentials: true }),
      ]);
      setBlogs(ar.data.blogs || []);
      setTrashedBlogs(tr.data.blogs || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleEditSave = async (blogId, data) => {
    await axios.put(`${API}/admin/blogs/${blogId}`, data, { withCredentials: true });
    await load();
  };

  const handleTogglePublish = async (blog) => {
    await axios.put(`${API}/admin/blogs/${blog.id}`, { published: !blog.published }, { withCredentials: true });
    await load();
  };

  const handleTrash = async (blogId) => {
    if (!window.confirm("Move this blog post to trash?")) return;
    await axios.put(`${API}/admin/blogs/${blogId}/trash`, {}, { withCredentials: true });
    await load();
  };

  const handleRestore = async (blogId) => {
    await axios.put(`${API}/admin/blogs/${blogId}/restore`, {}, { withCredentials: true });
    await load();
  };

  const handlePermanentDelete = async (blogId) => {
    if (!window.confirm("Permanently delete this blog post? This cannot be undone.")) return;
    await axios.delete(`${API}/admin/blogs/${blogId}`, { withCredentials: true });
    await load();
  };

  const filtered = search
    ? blogs.filter(b =>
        b.title?.toLowerCase().includes(search.toLowerCase()) ||
        b.category?.toLowerCase().includes(search.toLowerCase()) ||
        b.excerpt?.toLowerCase().includes(search.toLowerCase())
      )
    : blogs;

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-[#002147] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="admin-blog-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setView("active")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-[#002147]">{blogs.length}</p>
                <p className="text-xs text-slate-500 mt-0.5">Blog Posts</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${view === "active" ? "bg-indigo-100" : "bg-slate-100"}`}>
                <PenLine className={`h-5 w-5 ${view === "active" ? "text-indigo-600" : "text-slate-400"}`} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setView("trash")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-red-500">{trashedBlogs.length}</p>
                <p className="text-xs text-slate-500 mt-0.5">Trash</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${view === "trash" ? "bg-red-100" : "bg-slate-100"}`}>
                <Trash2 className={`h-5 w-5 ${view === "trash" ? "text-red-500" : "text-slate-400"}`} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-emerald-600">{blogs.filter(b => b.published).length}</p>
                <p className="text-xs text-slate-500 mt-0.5">Published</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Eye className="h-5 w-5 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setView("rss")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-orange-500"><Rss className="h-6 w-6" /></p>
                <p className="text-xs text-slate-500 mt-0.5">RSS Feeds</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${view === "rss" ? "bg-orange-100" : "bg-slate-100"}`}>
                <Rss className={`h-5 w-5 ${view === "rss" ? "text-orange-500" : "text-slate-400"}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* RSS View */}
      {view === "rss" && <RssSection onImported={load} />}

      {/* Blog List */}
      {view !== "rss" && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-base">
                {view === "active" ? `Blog Posts (${filtered.length})` : `Trash (${trashedBlogs.length})`}
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                {view === "active" && (
                  <Button data-testid="admin-gen-blog-btn" size="sm" onClick={() => setGenOpen(true)} className="bg-[#D4AF37] hover:bg-[#B5952F] text-[#002147] text-xs h-8 gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" /> Generate Post
                  </Button>
                )}
                {view === "active" && (
                  <div className="relative">
                    <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input data-testid="blog-search" placeholder="Search title, category..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 w-56 text-xs" />
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {view === "active" && (
              filtered.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <PenLine className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">{blogs.length === 0 ? "No blog posts yet" : "No posts match your search"}</p>
                  {blogs.length === 0 && (
                    <Button size="sm" onClick={() => setGenOpen(true)} className="mt-4 bg-[#D4AF37] hover:bg-[#B5952F] text-[#002147] text-xs">
                      <Sparkles className="h-3.5 w-3.5 mr-1" /> Generate Your First Post
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {filtered.map(blog => <BlogRow key={blog.id} blog={blog} onEdit={setEditBlog} onTrash={handleTrash} onTogglePublish={handleTogglePublish} />)}
                </div>
              )
            )}
            {view === "trash" && (
              trashedBlogs.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Archive className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Trash is empty</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {trashedBlogs.map(blog => <TrashedBlogRow key={blog.id} blog={blog} onRestore={handleRestore} onDelete={handlePermanentDelete} />)}
                </div>
              )
            )}
          </CardContent>
        </Card>
      )}

      <EditBlogDialog blog={editBlog} open={!!editBlog} onClose={() => setEditBlog(null)} onSave={handleEditSave} />
      <GenerateBlogDialog open={genOpen} onClose={() => setGenOpen(false)} onGenerated={load} />
    </div>
  );
}
